import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Trophy, Flame, Zap, Rocket, CalendarDays, Gift, ArrowUpRight, Sparkles, Medal, TrendingUp, Droplets } from "lucide-react";
import { toast } from "sonner";
import { api, shortAddr, avatarUrl } from "@/lib/api";
import { useWallet } from "@/context/WalletContext";
import { fmtNum, fmtUsd } from "@/lib/format";

function Countdown({ ends }) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const tick = () => {
      const ms = new Date(ends).getTime() - Date.now();
      if (ms <= 0) return setLeft("Ended");
      const d = Math.floor(ms / 86400000);
      const h = Math.floor((ms % 86400000) / 3600000);
      const m = Math.floor((ms % 3600000) / 60000);
      setLeft(`${d}d ${h}h ${m}m`);
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [ends]);
  return <span className="font-mono font-bold text-[#1EC62E]" data-testid="season-countdown">{left}</span>;
}

const RANK_STYLE = ["#FFD24A", "#CBD5E1", "#E8995A"]; // gold / silver / bronze

function Sparkline({ values, width = 150, height = 44 }) {
  if (!values || values.length < 2) return null;
  const max = Math.max(...values), min = Math.min(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return [x, y];
  });
  const line = pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `0,${height} ${line} ${width},${height}`;
  const [lx, ly] = pts[pts.length - 1];
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible" aria-hidden="true" data-testid="pool-sparkline">
      <defs>
        <linearGradient id="sparkStroke" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#15A525" /><stop offset="100%" stopColor="#7CF24E" /></linearGradient>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1EC62E" stopOpacity="0.35" /><stop offset="100%" stopColor="#1EC62E" stopOpacity="0" /></linearGradient>
      </defs>
      <polygon points={area} fill="url(#sparkFill)" />
      <polyline points={line} fill="none" stroke="url(#sparkStroke)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lx} cy={ly} r="2.6" fill="#7CF24E" style={{ filter: "drop-shadow(0 0 4px rgba(124,242,78,0.9))" }} />
    </svg>
  );
}

export default function Points() {
  const { connected, address } = useWallet();
  const [season, setSeason] = useState(null);
  const [board, setBoard] = useState([]);
  const [me, setMe] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [suiUsd, setSuiUsd] = useState(null);
  const [poolHist, setPoolHist] = useState([]);

  const loadBoard = useCallback(async () => {
    try { const { data } = await api.get("/points/leaderboard", { params: { limit: 100 } }); setBoard(data); } catch (e) {}
  }, []);
  const loadMe = useCallback(async () => {
    if (!address) { setMe(null); return; }
    try { const { data } = await api.get(`/points/${address}`); setMe(data); } catch (e) {}
  }, [address]);

  useEffect(() => { api.get("/points/season").then(({ data }) => setSeason(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/sui-price").then(({ data }) => { if (data?.usd) setSuiUsd(data.usd); }).catch(() => {}); }, []);
  useEffect(() => { api.get("/points/pool-history").then(({ data }) => setPoolHist(data || [])).catch(() => {}); }, []);
  useEffect(() => { loadBoard(); }, [loadBoard]);
  useEffect(() => { loadMe(); }, [loadMe]);

  const claimDaily = async () => {
    if (!connected) return toast.error("Connect your wallet first");
    setClaiming(true);
    try {
      const { data } = await api.post("/points/login", { address });
      if (data.awarded) toast.success(`+${data.points} points · ${data.streak}-day streak 🔥`);
      else toast("Daily bonus already claimed today — come back tomorrow!");
      await Promise.all([loadMe(), loadBoard()]);
    } catch (e) { toast.error("Could not claim daily bonus"); }
    finally { setClaiming(false); }
  };

  const rules = season?.rules || {};
  const earnCards = [
    { icon: Zap, color: "#1EC62E", title: "Trade", desc: `Earn ${rules.per_sui ?? 10} points for every 1 SUI you buy or sell` },
    { icon: Rocket, color: "#00E5FF", title: "Create a coin", desc: `Launch a token and get ${rules.create_coin ?? 500} points instantly` },
    { icon: Flame, color: "#FF6B00", title: "Daily streak", desc: `${rules.daily_login ?? 25} points a day, +${rules.streak_bonus ?? 5}/day streak bonus` },
  ];

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="points-page">
      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden rounded-3xl border border-[#1EC62E]/30 mb-7 px-6 py-8 sm:px-10 sm:py-10" data-testid="season-hero">
        <div className="hero-mesh" aria-hidden="true" />
        <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full opacity-25 blur-3xl" style={{ background: "radial-gradient(circle,#1EC62E,transparent 70%)" }} aria-hidden="true" />
        <div className="relative">
          <span className="eyebrow inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Airdrop Season · Live</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight mt-3 leading-[1.05]">
            Earn points. <span className="gradient-text">Climb the board.</span>
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base mt-3 max-w-xl leading-relaxed">
            Every trade, launch and daily check-in earns <b className="text-foreground">{season?.name || "Season 1"}</b> points. A wallet snapshot is taken for the airdrop — the higher you rank, the bigger your share.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-7">
            <div className="stat-pill" data-testid="season-stat-ends">
              <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0" style={{ background: "#1EC62E1f" }}><CalendarDays className="w-4 h-4 text-[#1EC62E]" /></span>
              <div className="min-w-0"><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Season ends</div><div className="font-display font-extrabold text-base sm:text-lg leading-tight">{season ? <Countdown ends={season.ends} /> : "—"}</div></div>
            </div>
            <div className="stat-pill" data-testid="season-stat-participants">
              <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0" style={{ background: "#00E5FF1f" }}><Trophy className="w-4 h-4 text-[#00E5FF]" /></span>
              <div className="min-w-0"><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Participants</div><div className="font-display font-extrabold text-base sm:text-lg leading-tight">{season ? Number(season.participants).toLocaleString() : "—"}</div></div>
            </div>
            <div className="stat-pill" data-testid="season-stat-total">
              <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0" style={{ background: "#FFB8001f" }}><Zap className="w-4 h-4 text-[#FFB800]" /></span>
              <div className="min-w-0"><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Points issued</div><div className="font-display font-extrabold text-base sm:text-lg leading-tight">{season ? fmtNum(season.total_points, 0) : "—"}</div></div>
            </div>
            <button onClick={claimDaily} disabled={claiming} data-testid="claim-daily-button" className="stat-pill text-left hover:border-[#1EC62E]/50 transition-colors disabled:opacity-60">
              <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0" style={{ background: "#FF6B001f" }}><Gift className="w-4 h-4 text-[#FF6B00]" /></span>
              <div className="min-w-0"><div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Daily bonus</div><div className="font-display font-extrabold text-base sm:text-lg leading-tight text-[#1EC62E]">{claiming ? "…" : "Claim"}</div></div>
            </button>
          </div>
        </div>
      </section>

      {/* ---------- S1 AIRDROP POOL ---------- */}
      <div className="relative overflow-hidden rounded-3xl border border-[#1EC62E]/40 mb-7 p-5 sm:p-6" data-testid="airdrop-pool-card" style={{ background: "linear-gradient(120deg,rgba(30,198,46,0.12),rgba(0,229,255,0.06) 60%,transparent)" }}>
        <div className="absolute -bottom-16 -left-10 w-64 h-64 rounded-full opacity-25 blur-3xl" style={{ background: "radial-gradient(circle,#1EC62E,transparent 70%)" }} aria-hidden="true" />
        <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <span className="grid place-items-center w-14 h-14 rounded-2xl shrink-0 shadow-[0_0_24px_rgba(30,198,46,0.4)]" style={{ background: "linear-gradient(135deg,#1EC62E,#7CF24E)" }}>
              <Droplets className="w-7 h-7 text-[#04121f]" />
            </span>
            <div className="min-w-0">
              <div className="eyebrow">S1 Airdrop Pool</div>
              <div className="flex items-baseline gap-2 flex-wrap mt-1">
                <span className="font-display font-extrabold text-3xl sm:text-4xl" data-testid="airdrop-pool-sui">{season ? fmtNum(season.airdrop_pool_sui || 0, 3) : "—"}</span>
                <span className="font-mono text-sm text-[#1EC62E]">SUI</span>
                {suiUsd != null && season != null && <span className="font-mono text-sm text-muted-foreground">· {fmtUsd((season.airdrop_pool_sui || 0) * suiUsd)}</span>}
              </div>
            </div>
          </div>
          <div className="sm:text-right shrink-0">
            {poolHist.length >= 1
              ? <div className="flex sm:justify-end mb-2"><Sparkline values={[0, ...poolHist.map((h) => h.pool_sui)]} /></div>
              : <div className="text-[11px] text-muted-foreground mb-2 sm:text-right" data-testid="pool-sparkline-empty">Chart builds as trades come in</div>}
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1EC62E]/10 border border-[#1EC62E]/30 text-[#1EC62E] text-xs font-bold">
              <ArrowUpRight className="w-3.5 h-3.5" /> +{season?.rules?.pool_rate_pct ?? 0.05}% of every trade
            </div>
            <p className="text-[11px] text-muted-foreground mt-2 max-w-[240px] sm:ml-auto leading-snug">Grows with every buy &amp; sell. Split among points holders at the season-end snapshot.</p>
          </div>
        </div>
      </div>

      {/* ---------- YOUR POINTS ---------- */}
      {connected ? (
        <div className="card-surface relative overflow-hidden p-5 sm:p-6 mb-7" data-testid="my-points-card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="flex-1 min-w-0">
              <div className="eyebrow mb-1">Your points</div>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="font-display font-extrabold text-4xl sm:text-5xl gradient-text" data-testid="my-points-value">{fmtNum(me?.points || 0, 0)}</span>
                {me?.rank && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1EC62E]/10 border border-[#1EC62E]/30 text-[#1EC62E] text-xs font-bold" data-testid="my-rank"><Medal className="w-3.5 h-3.5" /> Rank #{me.rank}</span>}
                {me?.streak > 0 && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/30 text-[#FF6B00] text-xs font-bold"><Flame className="w-3.5 h-3.5" /> {me.streak}-day streak</span>}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 shrink-0">
              {[
                { label: "From trades", value: fmtNum(me?.trade_points || 0, 0), color: "#1EC62E" },
                { label: "From launches", value: fmtNum(me?.create_points || 0, 0), color: "#00E5FF" },
                { label: "From streak", value: fmtNum(me?.login_points || 0, 0), color: "#FF6B00" },
              ].map((s) => (
                <div key={s.label} className="rounded-xl bg-white/[0.03] border border-white/10 px-3 py-2 text-center min-w-[88px]">
                  <div className="font-display font-extrabold text-lg" style={{ color: s.color }}>{s.value}</div>
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="card-surface p-6 text-center mb-7" data-testid="points-connect-prompt">
          <Trophy className="w-8 h-8 mx-auto mb-2 text-[#1EC62E]" />
          <div className="font-display font-bold">Connect your wallet to start earning</div>
          <p className="text-xs text-muted-foreground mt-1">Your points and leaderboard rank appear here once connected.</p>
        </div>
      )}

      {/* ---------- HOW TO EARN ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-7" data-testid="earn-cards">
        {earnCards.map((c) => (
          <div key={c.title} className="card-surface p-4 flex items-start gap-3">
            <span className="grid place-items-center w-10 h-10 rounded-xl shrink-0" style={{ background: `${c.color}1f` }}><c.icon className="w-5 h-5" style={{ color: c.color }} /></span>
            <div>
              <div className="font-display font-bold text-sm">{c.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5 leading-snug">{c.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ---------- LEADERBOARD ---------- */}
      <div className="card-surface p-4 sm:p-5" data-testid="leaderboard-card">
        <div className="flex items-center justify-between mb-4">
          <div className="eyebrow inline-flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Leaderboard</div>
          <span className="text-[11px] text-muted-foreground">Top {board.length}</span>
        </div>

        {board.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground text-sm"><TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-40" />No points earned yet. Be the first — trade or launch a coin!</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leaderboard-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-white/10">
                  <th className="text-left font-medium py-2 pl-1 w-12">#</th>
                  <th className="text-left font-medium py-2">Trader</th>
                  <th className="text-right font-medium py-2 hidden sm:table-cell">Volume</th>
                  <th className="text-right font-medium py-2 hidden sm:table-cell">Trades</th>
                  <th className="text-right font-medium py-2 hidden md:table-cell">Coins</th>
                  <th className="text-right font-medium py-2 pr-1">Points</th>
                </tr>
              </thead>
              <tbody>
                {board.map((r) => {
                  const isMe = address && r.address?.toLowerCase() === address.toLowerCase();
                  const medal = r.rank <= 3 ? RANK_STYLE[r.rank - 1] : null;
                  return (
                    <tr key={r.address} data-testid={`leaderboard-row-${r.rank}`} className={`border-b border-white/5 transition-colors ${isMe ? "bg-[#1EC62E]/[0.06]" : "hover:bg-white/[0.03]"}`}>
                      <td className="py-2.5 pl-1">
                        {medal
                          ? <span className="inline-grid place-items-center w-6 h-6 rounded-full font-display font-extrabold text-[11px] text-black" style={{ background: medal }}>{r.rank}</span>
                          : <span className="font-mono text-muted-foreground">{r.rank}</span>}
                      </td>
                      <td className="py-2.5">
                        <Link to={`/u/${r.address}`} className="flex items-center gap-2 group">
                          {avatarUrl(r.avatar_path)
                            ? <img src={avatarUrl(r.avatar_path)} alt="" className="w-7 h-7 rounded-full object-cover ring-1 ring-white/10" />
                            : <span className="w-7 h-7 rounded-full grid place-items-center text-[10px] font-bold text-black" style={{ background: "linear-gradient(135deg,#1EC62E,#7CF24E)" }}>{(r.username || r.address || "?").slice(2, 4).toUpperCase()}</span>}
                          <span className="font-mono text-xs group-hover:text-[#1EC62E] transition-colors truncate max-w-[160px]">{r.username ? `@${r.username}` : shortAddr(r.address)}{isMe && <span className="ml-1.5 text-[#1EC62E]">· you</span>}</span>
                        </Link>
                      </td>
                      <td className="py-2.5 text-right font-mono text-muted-foreground hidden sm:table-cell">{fmtNum(r.volume_sui, 1)}</td>
                      <td className="py-2.5 text-right font-mono text-muted-foreground hidden sm:table-cell">{r.trades}</td>
                      <td className="py-2.5 text-right font-mono text-muted-foreground hidden md:table-cell">{r.coins_created}</td>
                      <td className="py-2.5 pr-1 text-right font-display font-extrabold text-[#1EC62E]">{fmtNum(r.points, 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-center text-[11px] text-muted-foreground mt-5 inline-flex items-center gap-1 w-full justify-center">
        <ArrowUpRight className="w-3.5 h-3.5" /> Points are an engagement reward. A wallet snapshot will be taken at season end for the airdrop.
      </p>
    </div>
  );
}
